import { z } from 'zod';

/**
 * Search-param schema for the `/client-requests` route.
 *
 * Persists filter state (search, status/type/sub-type, responsible/investor/
 * account ids, date range) in the URL so it survives reloads and is
 * shareable, per the FE Requirements spec ("filter persistence: stored in
 * the URL (route), persist across sessions").
 *
 * Every field is `.optional().catch(undefined)` rather than `.catch(default)`:
 * with a plain `.catch(default)`, a missing key is indistinguishable from a
 * malformed one, so TanStack Router's `validateSearch` re-fills it with its
 * default value on *every* navigation — including when `useUrlFilters`
 * deliberately omits a default-valued key to produce a clean URL after
 * "Reset". `.optional()` lets an absent key stay absent (only genuinely
 * malformed values fall back via `.catch(undefined)`, treated the same as
 * absent); `useUrlFilters` fills in the actual default when reading.
 */
export const clientRequestsSearchSchema = z.object({
  search: z.string().optional().catch(undefined),
  statuses: z.array(z.string()).optional().catch(undefined),
  /** DEV-4067: "Show deleted" toggle, separate from `statuses`. */
  deleted: z.boolean().optional().catch(undefined),
  types: z.array(z.string()).optional().catch(undefined),
  subTypes: z.array(z.string()).optional().catch(undefined),
  responsibleIds: z.array(z.string()).optional().catch(undefined),
  investorIds: z.array(z.string()).optional().catch(undefined),
  accountIds: z.array(z.string()).optional().catch(undefined),
  /** Active date-range preset id (e.g. `'7d'`), `'custom'`, or `null` for "All". */
  dateRangePreset: z.string().nullable().optional().catch(undefined),
  /** Resolved ISO (`yyyy-MM-dd`) bounds, kept in sync with `dateRangePreset`. */
  dateFrom: z.string().optional().catch(undefined),
  dateTo: z.string().optional().catch(undefined),
  /** One-shot deep-link target (e.g. from a notification) — opens the details drawer for this
   *  request on mount, then is stripped from the URL. Not a persisted filter. */
  requestId: z.string().optional().catch(undefined),
});

export type ClientRequestsSearch = z.infer<typeof clientRequestsSearchSchema>;
